import React, { useState, useEffect } from 'react';
import { Box, Button, FormControl, InputLabel, Select, MenuItem, Stack } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useQuery } from '@tanstack/react-query';
import { listChains, Chain } from '../services/chainService';
import { listRegions, Region } from '../services/regionService';
import { listSettlements, Settlement } from '../services/settlementService';
import { StoreFilters as StoreFiltersType } from '../services/storeService';

interface StoreFiltersProps {
  filters: StoreFiltersType;
  onFiltersChange: (filters: StoreFiltersType) => void;
}

const StoreFilters: React.FC<StoreFiltersProps> = ({ filters, onFiltersChange }) => {
  const [localFilters, setLocalFilters] = useState<StoreFiltersType>(filters);

  const { data: chains = [] } = useQuery({ queryKey: ['chains'], queryFn: listChains });
  const { data: regions = [] } = useQuery({ queryKey: ['regions'], queryFn: listRegions });
  const { data: settlements = [] } = useQuery({ 
    queryKey: ['settlements', localFilters.region_id], 
    queryFn: () => listSettlements(localFilters.region_id),
    enabled: !!localFilters.region_id
  });

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof StoreFiltersType, value: string | number | undefined) => {
    const newFilters = { ...localFilters };
    
    if (value === '' || value === undefined) {
      delete newFilters[key];
    } else {
      newFilters[key] = value as any;
    }

    // If region changes, clear settlement filter
    if (key === 'region_id') {
      delete newFilters.settlement_id;
    }

    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
  };

  const handleClearFilters = () => {
    const emptyFilters = {};
    setLocalFilters(emptyFilters);
    onFiltersChange(emptyFilters);
  };

  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Chain</InputLabel>
          <Select
            value={localFilters.chain_id || ''}
            onChange={(e) => handleFilterChange('chain_id', e.target.value)}
            label="Chain"
          >
            <MenuItem value="">All Chains</MenuItem>
            {chains.map((chain: Chain) => (
              <MenuItem key={chain.id} value={chain.id}>
                {chain.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Region</InputLabel>
          <Select
            value={localFilters.region_id || ''}
            onChange={(e) => handleFilterChange('region_id', e.target.value ? Number(e.target.value) : undefined)}
            label="Region"
          >
            <MenuItem value="">All Regions</MenuItem>
            {regions.map((region: Region) => (
              <MenuItem key={region.id} value={region.id}>
                {region.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Town</InputLabel>
          <Select
            value={localFilters.settlement_id || ''}
            onChange={(e) => handleFilterChange('settlement_id', e.target.value ? Number(e.target.value) : undefined)}
            label="Town"
            disabled={!localFilters.region_id}
          >
            <MenuItem value="">All Towns</MenuItem>
            {settlements.map((settlement: Settlement) => (
              <MenuItem key={settlement.id} value={settlement.id}>
                {settlement.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            size="small"
          >
            Apply Filters
          </Button>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
            size="small"
          >
            Clear
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default StoreFilters; 